// cspell:disable
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  PurolatorServices,
  SpeeDeeServices,
  UpsServices,
  UspsServices,
} from "./carrier-service-enums";

export enum VehoServices {
  NextDay = "nextDay",
  SameDay = "sameDay",
  GroundPlus = "groundPlus",
  PremiumEconomy = "premiumEconomy",
  GroundPlusThree = "groundPlusThree",
}

export enum FirstMileServices {
  XParcelGround = "XParcelGround",
  XParcelPriority = "XParcelPriority",
  XParcelExpedited = "XParcelExpedited",
  XParcelExpeditedPlus = "XParcelExpeditedPlus",
}

export enum AustraliaPostServices {
  ParcelPost = "ParcelPost",
  ExpressPost = "ExpressPost",
  International = "International",
  Commercial = "Commercial",
  ParcelPostSignature = "ParcelPostSignature" /** We don't know why, but the docs say "ParcelPost" but actual rates append "ParcelPostSignature" */,
  ExpressPostSignature = "ExpressPostSignature",
  InternationalSignature = "InternationalSignature",
  CommercialSignature = "CommercialSignature",
  EParcel1 = "EParcel1",
  EParcelPostReturns = "EParcelPostReturns",
  ExpressParcelPostReturns = "ExpressParcelPostReturns",
  ExpressPostEParcel = "ExpressPostEParcel",
  InternationalEconomyAirmailParcels = "InternationalEconomyAirmailParcels",
  InternationalEconomyWSODREGPost = "InternationalEconomyWSODREGPost",
  InternationalExpressDocSecIDocs = "InternationalExpressDocSecIDocs",
  InternationalExpressMerchandise = "InternationalExpressMerchandise",
  InternationalExpressMerchECIMerch = "InternationalExpressMerchECIMerch",
  InternationalStandardPackTrack = "InternationalStandardPackTrack",
  ParcelPostExtra = "ParcelPostExtra",
  ParcelPostWinePlusSignature = "ParcelPostWinePlusSignature",
  ParcelPostWineSignature = "ParcelPostWineSignature",
  ExpressEparcelPostReturns = "ExpressEparcelPostReturns",
  EparcelPostReturns = "EparcelPostReturns",
  IntlExpressDocs = "IntlExpressDocs",
  IntlStandardPackTrack = "IntlStandardPackTrack",
  IntLStandardWithSignature = "IntLStandardWithSignature", // The capital L is not a mistake, that's what we get
  IntlExpressMerch = "IntlExpressMerch",
}

// Only AusPost services allowed for RETURN labels; others (e.g. outbound ParcelPost)
// go "unmanifested" and incur penalty fees. EasyPost uses the lowercase-p casing.
export const ALLOWED_AUSTRALIA_POST_RETURN_SERVICES: ReadonlySet<string> =
  new Set<string>([
    AustraliaPostServices.EparcelPostReturns,
    AustraliaPostServices.ExpressParcelPostReturns,
    AustraliaPostServices.ExpressEparcelPostReturns,
  ]);

export function isAllowedAustraliaPostReturnService(service: string): boolean {
  return ALLOWED_AUSTRALIA_POST_RETURN_SERVICES.has(service);
}

// Deny-list: only time-definite tiers (pricier duplicates of a standard service
// on the same quote) and medical specialty; anything else stays offered.
export const DISALLOWED_DHL_EXPRESS_RETURN_SERVICES: ReadonlySet<string> =
  new Set<string>([
    DhlExpressServices.Express900,
    DhlExpressServices.Express900NonDoc,
    DhlExpressServices.Express1030,
    DhlExpressServices.Express1030NonDoc,
    DhlExpressServices.Express1200,
    DhlExpressServices.Express1200NonDoc,
    DhlExpressServices.DomesticExpress1030,
    DhlExpressServices.DomesticExpress1200,
    DhlExpressServices.MedicalExpress,
    DhlExpressServices.MedicalExpressNonDoc,
  ]);

export function isAllowedDhlExpressReturnService(service: string): boolean {
  return !DISALLOWED_DHL_EXPRESS_RETURN_SERVICES.has(service);
}

export enum GlsServices {
  EarlyPriorityOvernight = "EarlyPriorityOvernight",
  PriorityOvernight = "PriorityOvernight",
  CaliforniaParcelService = "CaliforniaParcelService",
  SaturdayDeliveryService = "SaturdayDeliveryService",
  EarlySaturdayService = "EarlySaturdayService",
  NoonPriorityService = "NoonPriorityService",
}

export enum RoyalMailV3Services {
  RoyalMailV3 = "RoyalMailV3",
  EuropriorityDDP = "EuropriorityDDP",
  EuropriorityDDPComp1 = "EuropriorityDDPComp1",
  EuropriorityDDPComp2 = "EuropriorityDDPComp2",
  EuropriorityDDPComp3 = "EuropriorityDDPComp3",
  EuropriorityDTPIOSS = "EuropriorityDTPIOSS",
  EuropriorityDTPIOSSComp1 = "EuropriorityDTPIOSSComp1",
  EuropriorityDTPIOSSComp2 = "EuropriorityDTPIOSSComp2",
  EuropriorityDTPIOSSComp3 = "EuropriorityDTPIOSSComp3",
  Express10 = "Express10",
  Express10Comp1 = "Express10Comp1",
  Express10Comp2 = "Express10Comp2",
  Express10Comp3 = "Express10Comp3",
  Express24 = "Express24",
  Express24Comp1 = "Express24Comp1",
  Express24Comp2 = "Express24Comp2",
  Express24Comp3 = "Express24Comp3",
  Express24Weekend = "Express24Weekend",
  Express24WeekendComp1 = "Express24WeekendComp1",
  Express24WeekendComp2 = "Express24WeekendComp2",
  Express24WeekendComp3 = "Express24WeekendComp3",
  Express48 = "Express48",
  Express48Comp1 = "Express48Comp1",
  Express48Comp2 = "Express48Comp2",
  Express48Comp3 = "Express48Comp3",
  Express48Large = "Express48Large",
  Express48LargeComp1 = "Express48LargeComp1",
  Express48LargeComp2 = "Express48LargeComp2",
  Express48LargeComp3 = "Express48LargeComp3",
  ExpressAM = "ExpressAM",
  ExpressAMComp1 = "ExpressAMComp1",
  ExpressAMComp2 = "ExpressAMComp2",
  ExpressAMComp3 = "ExpressAMComp3",
  GlobalpriorityEurope = "GlobalpriorityEurope",
  GlobalpriorityEuropeComp1 = "GlobalpriorityEuropeComp1",
  GlobalpriorityEuropeComp2 = "GlobalpriorityEuropeComp2",
  GlobalpriorityEuropeComp3 = "GlobalpriorityEuropeComp3",
  GlobalpriorityROW = "GlobalpriorityROW",
  GlobalpriorityROWComp1 = "GlobalpriorityROWComp1",
  GlobalpriorityROWComp2 = "GlobalpriorityROWComp2",
  GlobalpriorityROWComp3 = "GlobalpriorityROWComp3",
  InternationalBusinessParcelsTrackedPriority = "InternationalBusinessParcelsTrackedPriority",
  Tracked24HighVolumePIN = "Tracked24HighVolumePIN",
  Tracked24PIN = "Tracked24PIN",
  Tracked48HighVolumePIN = "Tracked48HighVolumePIN",
  Tracked48PIN = "Tracked48PIN",
  TrackedImport24HighVolumePIN = "TrackedImport24HighVolumePIN",
  TrackedImport48HighVolumePIN = "TrackedImport48HighVolumePIN",
  DEImportRoyalMail24PlusParcelsWeekendService = "DEImportRoyalMail24PlusParcelsWeekendService",
  DEImportStandard24LargeLetter = "DEImportStandard24LargeLetter",
  DEImportStandard24LargeLetterWeekendService = "DEImportStandard24LargeLetterWeekendService",
  DEImportStandard24Parcel = "DEImportStandard24Parcel",
  DEImportStandard24ParcelWeekendService = "DEImportStandard24ParcelWeekendService",
  DEImportStandard48LargeLetter = "DEImportStandard48LargeLetter",
  DEImportStandard48LargeLetterWeekendService = "DEImportStandard48LargeLetterWeekendService",
  DEImportTracked24ParcelBoxableHighVolumeWeekendService = "DEImportTracked24ParcelBoxableHighVolumeWeekendService",
  DEImportTracked24ParcelBoxableWeekendService = "DEImportTracked24ParcelBoxableWeekendService",
  DEImportTracked24ParcelHighVolumeWeekendService = "DEImportTracked24ParcelHighVolumeWeekendService",
  DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE",
  DEImportTracked48ParcelBoxableHighVolumeWeekendService = "DEImportTracked48ParcelBoxableHighVolumeWeekendService",
  DEImportTracked48ParcelBoxableWeekendService = "DEImportTracked48ParcelBoxableWeekendService",
  DEImportTracked48ParcelHighVolumeWeekendService = "DEImportTracked48ParcelHighVolumeWeekendService",
  DEImportTracked48SignatureParcelHighVolumeAGE = "DEImportTracked48SignatureParcelHighVolumeAGE",
  DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE",
  DEImporttoEUMaxSortLargeLetter = "DEImporttoEUMaxSortLargeLetter",
  DEImporttoEUMaxSortParcel = "DEImporttoEUMaxSortParcel",
  DEImporttoEUSignedParcel = "DEImporttoEUSignedParcel",
  DEImporttoEUTrackedHighVolLargeLetter = "DEImporttoEUTrackedHighVolLargeLetter",
  DEImporttoEUTrackedParcel = "DEImporttoEUTrackedParcel",
  DEImporttoEUTrackedSignedLargeLetter = "DEImporttoEUTrackedSignedLargeLetter",
  DEImporttoEUTrackedSignedParcel = "DEImporttoEUTrackedSignedParcel",
  HMForcesMail = "HMForcesMail",
  HMForcesSignedFor = "HMForcesSignedFor",
  HMForcesSpecialDelivery1000 = "HMForcesSpecialDelivery1000",
  HMForcesSpecialDelivery2500 = "HMForcesSpecialDelivery2500",
  HMForcesSpecialDelivery500 = "HMForcesSpecialDelivery500",
  ImportDETracked24 = "ImportDETracked24",
  ImportDETracked24HighVolume = "ImportDETracked24HighVolume",
  ImportDETracked24Letterboxable = "ImportDETracked24Letterboxable",
  ImportDETracked24LetterboxableHighVolume = "ImportDETracked24LetterboxableHighVolume",
  ImportDETracked24SignatureHighVolumeAGE = "ImportDETracked24SignatureHighVolumeAGE",
  ImportDETracked24SignatureStandardAGE = "ImportDETracked24SignatureStandardAGE",
  ImportDETracked48 = "ImportDETracked48",
  ImportDETracked48HighVolume = "ImportDETracked48HighVolume",
  ImportDETracked48Letterboxable = "ImportDETracked48Letterboxable",
  ImportDETracked48LetterboxableHighVolume = "ImportDETracked48LetterboxableHighVolume",
  ImportDETrackedReturns24 = "ImportDETrackedReturns24",
  ImportDETrackedReturns48 = "ImportDETrackedReturns48",
  InternationalBusinessMailLargeLetterCountryPricedPriority = "InternationalBusinessMailLargeLetterCountryPricedPriority",
  InternationalBusinessMailLargeLetterCountryPricedPriorityETOE = "InternationalBusinessMailLargeLetterCountryPricedPriorityETOE",
  InternationalBusinessMailLargeLetterCountrySortPriority = "InternationalBusinessMailLargeLetterCountrySortPriority",
  InternationalBusinessMailLargeLetterMaxSortPriorityService = "InternationalBusinessMailLargeLetterMaxSortPriorityService",
  InternationalBusinessMailLargeLetterMaxSortResidueStandard = "InternationalBusinessMailLargeLetterMaxSortResidueStandard",
  InternationalBusinessMailLargeLetterMaxSortStandardService = "InternationalBusinessMailLargeLetterMaxSortStandardService",
  InternationalBusinessMailLargeLetterSemiTracked = "InternationalBusinessMailLargeLetterSemiTracked",
  InternationalBusinessMailLargeLetterZeroSortPriority = "InternationalBusinessMailLargeLetterZeroSortPriority",
  InternationalBusinessMailLargeLetterZeroSortPriorityMachine = "InternationalBusinessMailLargeLetterZeroSortPriorityMachine",
  InternationalBusinessMailLargeLetterZoneSortPriority = "InternationalBusinessMailLargeLetterZoneSortPriority",
  InternationalBusinessMailLargeLetterZoneSortPriorityMachine = "InternationalBusinessMailLargeLetterZoneSortPriorityMachine",
  InternationalBusinessMailLetterMaxSortResidueStandard = "InternationalBusinessMailLetterMaxSortResidueStandard",
  InternationalBusinessMailLettersMaxSortStandard = "InternationalBusinessMailLettersMaxSortStandard",
  InternationalBusinessMailLettersZeroSortPriority = "InternationalBusinessMailLettersZeroSortPriority",
  InternationalBusinessMailLettersZoneSortPriority = "InternationalBusinessMailLettersZoneSortPriority",
  InternationalBusinessMailSignedCountryPriced = "InternationalBusinessMailSignedCountryPriced",
  InternationalBusinessMailSignedExtraCompensationCountryPriced = "InternationalBusinessMailSignedExtraCompensationCountryPriced",
  InternationalBusinessMailSignedHighVolCountryPriced = "InternationalBusinessMailSignedHighVolCountryPriced",
  InternationalBusinessMailSignedZoneSort = "InternationalBusinessMailSignedZoneSort",
  InternationalBusinessMailTrackedCountryPriced = "InternationalBusinessMailTrackedCountryPriced",
  InternationalBusinessMailTrackedHighVolCountryPriced = "InternationalBusinessMailTrackedHighVolCountryPriced",
  InternationalBusinessMailTrackedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedCountryPriced = "InternationalBusinessMailTrackedSignedCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolCountryPriced = "InternationalBusinessMailTrackedSignedHighVolCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedZoneSort = "InternationalBusinessMailTrackedSignedZoneSort",
  InternationalBusinessMailTrackedZoneSort = "InternationalBusinessMailTrackedZoneSort",
  InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE = "InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE",
  InternationalBusinessParcelPriorityCountryPricedBoxable = "InternationalBusinessParcelPriorityCountryPricedBoxable",
  InternationalBusinessParcelPriorityCountryPricedBoxableDDP = "InternationalBusinessParcelPriorityCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPriced = "InternationalBusinessParcelTrackedCountryPriced",
  InternationalBusinessParcelTrackedCountryPricedBoxable = "InternationalBusinessParcelTrackedCountryPricedBoxable",
  InternationalBusinessParcelTrackedCountryPricedBoxableDDP = "InternationalBusinessParcelTrackedCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp = "InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp",
  InternationalBusinessParcels = "InternationalBusinessParcels",
  InternationalBusinessParcelsPrintDirectPriority = "InternationalBusinessParcelsPrintDirectPriority",
  InternationalBusinessParcelsPrintDirectStandard = "InternationalBusinessParcelsPrintDirectStandard",
  InternationalBusinessParcelsSignedCountryPriced = "InternationalBusinessParcelsSignedCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationZoneSort = "InternationalBusinessParcelsSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsSignedZoneSort = "InternationalBusinessParcelsSignedZoneSort",
  InternationalBusinessParcelsTrackedCountryPriced = "InternationalBusinessParcelsTrackedCountryPriced",
  InternationalBusinessParcelsTrackedCountryPricedETOE = "InternationalBusinessParcelsTrackedCountryPricedETOE",
  InternationalBusinessParcelsTrackedDirectIrelandCountry = "InternationalBusinessParcelsTrackedDirectIrelandCountry",
  InternationalBusinessParcelsTrackedExtraCompCountryPriced = "InternationalBusinessParcelsTrackedExtraCompCountryPriced",
  InternationalBusinessParcelsTrackedExtraCompZoneSort = "InternationalBusinessParcelsTrackedExtraCompZoneSort",
  InternationalBusinessParcelsTrackedSignedCountryPriced = "InternationalBusinessParcelsTrackedSignedCountryPriced",
  InternationalBusinessParcelsTrackedSignedCountryPricedETOE = "InternationalBusinessParcelsTrackedSignedCountryPricedETOE",
  InternationalBusinessParcelsTrackedSignedDDP = "InternationalBusinessParcelsTrackedSignedDDP",
  InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort = "InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsTrackedSignedZoneSort = "InternationalBusinessParcelsTrackedSignedZoneSort",
  InternationalBusinessParcelsTrackedZoneSort = "InternationalBusinessParcelsTrackedZoneSort",
  InternationalBusinessParcelsZeroSortLowVolPriority = "InternationalBusinessParcelsZeroSortLowVolPriority",
  InternationalBusinessParcelsZeroSortPriority = "InternationalBusinessParcelsZeroSortPriority",
  InternationalBusinessParcelsZeroSortPriorityETOE = "InternationalBusinessParcelsZeroSortPriorityETOE",
  InternationalBusinessParcelsZoneSortPriorityService = "InternationalBusinessParcelsZoneSortPriorityService",
  InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE = "InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceMaxSortLetter = "InternationalBusinessPersonalCorrespondenceMaxSortLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter",
  InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE",
  InternationalBusinessPrintedMatterPacket = "InternationalBusinessPrintedMatterPacket",
  InternationalBusinessTrackedExpressNPC = "InternationalBusinessTrackedExpressNPC",
  InternationalEconomyOnAccount = "InternationalEconomyOnAccount",
  InternationalSignedOnAccount = "InternationalSignedOnAccount",
  InternationalSignedOnAccountExtraComp = "InternationalSignedOnAccountExtraComp",
  InternationalStandardOnAccount = "InternationalStandardOnAccount",
  InternationalTrackedOnAccount = "InternationalTrackedOnAccount",
  InternationalTrackedOnAccountExtraComp = "InternationalTrackedOnAccountExtraComp",
  InternationalTrackedParcels30kg = "InternationalTrackedParcels30kg",
  InternationalTrackedParcels30kgCPrio = "InternationalTrackedParcels30kgCPrio",
  InternationalTrackedParcels30kgETOEC = "InternationalTrackedParcels30kgETOEC",
  InternationalTrackedParcels30kgETOEE = "InternationalTrackedParcels30kgETOEE",
  InternationalTrackedParcels30kgExtraComp = "InternationalTrackedParcels30kgExtraComp",
  InternationalTrackedParcels30kgExtraCompETOEC = "InternationalTrackedParcels30kgExtraCompETOEC",
  InternationalTrackedParcels30kgExtraCompETOEE = "InternationalTrackedParcels30kgExtraCompETOEE",
  InternationalTrackedParcels30kgXCompCPrio = "InternationalTrackedParcels30kgXCompCPrio",
  InternationalTrackedSignedOnAccount = "InternationalTrackedSignedOnAccount",
  InternationalTrackedSignedOnAccountExtraComp = "InternationalTrackedSignedOnAccountExtraComp",
  ParcelpostFlatRateAnnual = "ParcelpostFlatRateAnnual",
  RM24LargeLetterAnnualFlatRate = "RM24LargeLetterAnnualFlatRate",
  RM24PresortedLargeLetterAnnualFlatRate = "RM24PresortedLargeLetterAnnualFlatRate",
  RM24PresortedParcelsAnnualFlatRate = "RM24PresortedParcelsAnnualFlatRate",
  RM48LargeLetterAnnualFlatRate = "RM48LargeLetterAnnualFlatRate",
  RM48PresortedLargeLetterAnnualFlatRate = "RM48PresortedLargeLetterAnnualFlatRate",
  RM48PresortedParcelsAnnualFlatRate = "RM48PresortedParcelsAnnualFlatRate",
  RoyalMail1stClass = "RoyalMail1stClass",
  RoyalMail1stClassLettersDailyRateservice = "RoyalMail1stClassLettersDailyRateservice",
  RoyalMail1stClassSignedFor = "RoyalMail1stClassSignedFor",
  RoyalMail24LargeLetterDailyRate = "RoyalMail24LargeLetterDailyRate",
  RoyalMail24LargeLetterFlatRate = "RoyalMail24LargeLetterFlatRate",
  RoyalMail24LargeLetterFlatRateService = "RoyalMail24LargeLetterFlatRateService",
  RoyalMail24PacketpostFlatRateService = "RoyalMail24PacketpostFlatRateService",
  RoyalMail24ParcelDailyRateService = "RoyalMail24ParcelDailyRateService",
  RoyalMail24ParcelSort8DailyRateService = "RoyalMail24ParcelSort8DailyRateService",
  RoyalMail24ParcelSort8FlatRateService = "RoyalMail24ParcelSort8FlatRateService",
  RoyalMail24ParcelsDailyRate = "RoyalMail24ParcelsDailyRate",
  RoyalMail24ParcelsFlatRate = "RoyalMail24ParcelsFlatRate",
  RoyalMail24PresortedLargeLetter = "RoyalMail24PresortedLargeLetter",
  RoyalMail24PresortedParcels = "RoyalMail24PresortedParcels",
  RoyalMail24SORT8LargeLetterAnnualFlatRate = "RoyalMail24SORT8LargeLetterAnnualFlatRate",
  RoyalMail24SORT8ParcelsAnnualFlatRate = "RoyalMail24SORT8ParcelsAnnualFlatRate",
  RoyalMail2ndClass = "RoyalMail2ndClass",
  RoyalMail2ndClassLettersDailyRateservice = "RoyalMail2ndClassLettersDailyRateservice",
  RoyalMail2ndClassSignedFor = "RoyalMail2ndClassSignedFor",
  RoyalMail48LargeLetterDailyRate = "RoyalMail48LargeLetterDailyRate",
  RoyalMail48LargeLetterFlatRate = "RoyalMail48LargeLetterFlatRate",
  RoyalMail48LargeLetterFlatRateService = "RoyalMail48LargeLetterFlatRateService",
  RoyalMail48PacketpostFlatRateService = "RoyalMail48PacketpostFlatRateService",
  RoyalMail48ParcelDailyRateService = "RoyalMail48ParcelDailyRateService",
  RoyalMail48ParcelSort8DailyRateService = "RoyalMail48ParcelSort8DailyRateService",
  RoyalMail48ParcelSort8FlatRateService = "RoyalMail48ParcelSort8FlatRateService",
  RoyalMail48ParcelsDailyRate = "RoyalMail48ParcelsDailyRate",
  RoyalMail48ParcelsFlatRate = "RoyalMail48ParcelsFlatRate",
  RoyalMail48PresortedLargeLetter = "RoyalMail48PresortedLargeLetter",
  RoyalMail48PresortedParcels = "RoyalMail48PresortedParcels",
  RoyalMail48SORT8LargeLetterAnnualFlatRate = "RoyalMail48SORT8LargeLetterAnnualFlatRate",
  RoyalMail48Sort8ParcelsAnnualFlatRate = "RoyalMail48Sort8ParcelsAnnualFlatRate",
  SpecialDeliveryGuaranteed1000 = "SpecialDeliveryGuaranteed1000",
  SpecialDeliveryGuaranteed2500 = "SpecialDeliveryGuaranteed2500",
  SpecialDeliveryGuaranteed750 = "SpecialDeliveryGuaranteed750",
  SpecialDeliveryGuaranteedAGE1000 = "SpecialDeliveryGuaranteedAGE1000",
  SpecialDeliveryGuaranteedAGE2500 = "SpecialDeliveryGuaranteedAGE2500",
  SpecialDeliveryGuaranteedAGE750 = "SpecialDeliveryGuaranteedAGE750",
  SpecialDeliveryGuaranteedBy1PM1000 = "SpecialDeliveryGuaranteedBy1PM1000",
  SpecialDeliveryGuaranteedBy1PM2500 = "SpecialDeliveryGuaranteedBy1PM2500",
  SpecialDeliveryGuaranteedBy1PM750 = "SpecialDeliveryGuaranteedBy1PM750",
  SpecialDeliveryGuaranteedBy1PMAGE1000 = "SpecialDeliveryGuaranteedBy1PMAGE1000",
  SpecialDeliveryGuaranteedBy1PMAGE2500 = "SpecialDeliveryGuaranteedBy1PMAGE2500",
  SpecialDeliveryGuaranteedBy1PMAGE750 = "SpecialDeliveryGuaranteedBy1PMAGE750",
  SpecialDeliveryGuaranteedBy1PMID1000 = "SpecialDeliveryGuaranteedBy1PMID1000",
  SpecialDeliveryGuaranteedBy1PMID2500 = "SpecialDeliveryGuaranteedBy1PMID2500",
  SpecialDeliveryGuaranteedBy1PMID750 = "SpecialDeliveryGuaranteedBy1PMID750",
  SpecialDeliveryGuaranteedBy9AM1000 = "SpecialDeliveryGuaranteedBy9AM1000",
  SpecialDeliveryGuaranteedBy9AM2500 = "SpecialDeliveryGuaranteedBy9AM2500",
  SpecialDeliveryGuaranteedBy9AM750 = "SpecialDeliveryGuaranteedBy9AM750",
  SpecialDeliveryGuaranteedBy9AMAGE1000 = "SpecialDeliveryGuaranteedBy9AMAGE1000",
  SpecialDeliveryGuaranteedBy9AMAGE2500 = "SpecialDeliveryGuaranteedBy9AMAGE2500",
  SpecialDeliveryGuaranteedBy9AMAGE750 = "SpecialDeliveryGuaranteedBy9AMAGE750",
  SpecialDeliveryGuaranteedBy9AMID1000 = "SpecialDeliveryGuaranteedBy9AMID1000",
  SpecialDeliveryGuaranteedBy9AMID2500 = "SpecialDeliveryGuaranteedBy9AMID2500",
  SpecialDeliveryGuaranteedBy9AMID750 = "SpecialDeliveryGuaranteedBy9AMID750",
  SpecialDeliveryGuaranteedID1000 = "SpecialDeliveryGuaranteedID1000",
  SpecialDeliveryGuaranteedID2500 = "SpecialDeliveryGuaranteedID2500",
  SpecialDeliveryGuaranteedID750 = "SpecialDeliveryGuaranteedID750",
  Tracked24 = "Tracked24",
  Tracked24HighVolume = "Tracked24HighVolume",
  Tracked24HighVolumeSignatureAGE = "Tracked24HighVolumeSignatureAGE",
  Tracked24SignatureAGE = "Tracked24SignatureAGE",
  Tracked48 = "Tracked48",
  Tracked48HighVolume = "Tracked48HighVolume",
  Tracked48HighVolumeSignatureAGE = "Tracked48HighVolumeSignatureAGE",
  Tracked48SignatureAGE = "Tracked48SignatureAGE",
  TrackedLetterBoxable24 = "TrackedLetterBoxable24",
  TrackedLetterBoxable24HighVolume = "TrackedLetterBoxable24HighVolume",
  TrackedLetterBoxable48 = "TrackedLetterBoxable48",
  TrackedLetterBoxable48HighVolume = "TrackedLetterBoxable48HighVolume",
  TrackedReturns24 = "TrackedReturns24",
  TrackedReturns48 = "TrackedReturns48",
}

export enum AsendiaServices {
  ADS = "ADS",
  AirFreightInbound = "AirFreightInbound",
  AirFreightOutbound = "AirFreightOutbound",
  AsendiaDomesticBoundPrinterMatterExpedited = "AsendiaDomesticBoundPrinterMatterExpedited",
  AsendiaDomesticBoundPrinterMatterGround = "AsendiaDomesticBoundPrinterMatterGround",
  AsendiaDomesticFlatsExpedited = "AsendiaDomesticFlatsExpedited",
  AsendiaDomesticFlatsGround = "AsendiaDomesticFlatsGround",
  AsendiaDomesticParcelGroundOver1lb = "AsendiaDomesticParcelGroundOver1lb",
  AsendiaDomesticParcelGroundUnder1lb = "AsendiaDomesticParcelGroundUnder1lb",
  AsendiaDomesticParcelMAXOver1lb = "AsendiaDomesticParcelMAXOver1lb",
  AsendiaDomesticParcelMAXUnder1lb = "AsendiaDomesticParcelMAXUnder1lb",
  AsendiaDomesticParcelOver1lbExpedited = "AsendiaDomesticParcelOver1lbExpedited",
  AsendiaDomesticParcelUnder1lbExpedited = "AsendiaDomesticParcelUnder1lbExpedited",
  AsendiaDomesticPromoParcelExpedited = "AsendiaDomesticPromoParcelExpedited",
  AsendiaDomesticPromoParcelGround = "AsendiaDomesticPromoParcelGround",
  BulkFreight = "BulkFreight",
  BusinessMailCanadaLettermail = "BusinessMailCanadaLettermail",
  BusinessMailCanadaLettermailMachineable = "BusinessMailCanadaLettermailMachineable",
  BusinessMailEconomy = "BusinessMailEconomy",
  BusinessMailEconomyLPWholesale = "BusinessMailEconomyLPWholesale",
  BusinessMailEconomySPWholesale = "BusinessMailEconomySPWholesale",
  BusinessMailIPA = "BusinessMailIPA",
  BusinessMailISAL = "BusinessMailISAL",
  BusinessMailPriority = "BusinessMailPriority",
  BusinessMailPriorityLPWholesale = "BusinessMailPriorityLPWholesale",
  BusinessMailPrioritySPWholesale = "BusinessMailPrioritySPWholesale",
  MarketingMailCanadaPersonalizedLCP = "MarketingMailCanadaPersonalizedLCP",
  MarketingMailCanadaPersonalizedMachineable = "MarketingMailCanadaPersonalizedMachineable",
  MarketingMailCanadaPersonalizedNDG = "MarketingMailCanadaPersonalizedNDG",
  MarketingMailEconomy = "MarketingMailEconomy",
  MarketingMailIPA = "MarketingMailIPA",
  MarketingMailISAL = "MarketingMailISAL",
  MarketingMailPriority = "MarketingMailPriority",
  PublicationsCanadaLCP = "PublicationsCanadaLCP",
  PublicationsCanadaNDG = "PublicationsCanadaNDG",
  PublicationsEconomy = "PublicationsEconomy",
  PublicationsIPA = "PublicationsIPA",
  PublicationsISAL = "PublicationsISAL",
  PublicationsPriority = "PublicationsPriority",
  ePAQElite = "ePAQElite",
  ePAQEliteCustom = "ePAQEliteCustom",
  ePAQEliteDAP = "ePAQEliteDAP",
  ePAQEliteDDP = "ePAQEliteDDP",
  ePAQEliteDDPOversized = "ePAQEliteDDPOversized",
  ePAQEliteDPD = "ePAQEliteDPD",
  ePAQEliteDirectAccessCanadaDDP = "ePAQEliteDirectAccessCanadaDDP",
  ePAQEliteOversized = "ePAQEliteOversized",
  ePAQPlus = "ePAQPlus",
  ePAQPlusCustom = "ePAQPlusCustom",
  ePAQPlusCustomsPrepaid = "ePAQPlusCustomsPrepaid",
  ePAQPlusDAP = "ePAQPlusDAP",
  ePAQPlusDDP = "ePAQPlusDDP",
  ePAQPlusEconomy = "ePAQPlusEconomy",
  ePAQPlusWholesale = "ePAQPlusWholesale",
  ePAQPlusePacket = "ePAQPlusePacket",
  ePAQPlusePacketCanadaCustomsPrePaid = "ePAQPlusePacketCanadaCustomsPrePaid",
  ePAQPlusePacketCanadaDDP = "ePAQPlusePacketCanadaDDP",
  ePAQReturnsDomestic = "ePAQReturnsDomestic",
  ePAQReturnsInternational = "ePAQReturnsInternational",
  ePAQSelect = "ePAQSelect",
  ePAQSelectCustom = "ePAQSelectCustom",
  ePAQSelectCustomsPrepaidByShopper = "ePAQSelectCustomsPrepaidByShopper",
  ePAQSelectDAP = "ePAQSelectDAP",
  ePAQSelectDDP = "ePAQSelectDDP",
  ePAQSelectDDPDirectAccess = "ePAQSelectDDPDirectAccess",
  ePAQSelectDirectAccess = "ePAQSelectDirectAccess",
  ePAQSelectDirectAccessCanadaDDP = "ePAQSelectDirectAccessCanadaDDP",
  ePAQSelectEconomy = "ePAQSelectEconomy",
  ePAQSelectOversized = "ePAQSelectOversized",
  ePAQSelectOversizedDDP = "ePAQSelectOversizedDDP",
  ePAQSelectPMEI = "ePAQSelectPMEI",
  ePAQSelectPMEICanadaCustomsPrePaid = "ePAQSelectPMEICanadaCustomsPrePaid",
  ePAQSelectPMEIPCPostage = "ePAQSelectPMEIPCPostage",
  ePAQSelectPMI = "ePAQSelectPMI",
  ePAQSelectPMICanadaCustomsPrepaid = "ePAQSelectPMICanadaCustomsPrepaid",
  ePAQSelectPMICanadaDDP = "ePAQSelectPMICanadaDDP",
  ePAQSelectPMINonPresort = "ePAQSelectPMINonPresort",
  ePAQSelectPMIPCPostage = "ePAQSelectPMIPCPostage",
  ePAQStandard = "ePAQStandard",
  ePAQStandardCustom = "ePAQStandardCustom",
  ePAQStandardEconomy = "ePAQStandardEconomy",
  ePAQStandardIPA = "ePAQStandardIPA",
  ePAQStandardISAL = "ePAQStandardISAL",
  ePaqSelectPMEINonPresort = "ePaqSelectPMEINonPresort",
  ePAQSelectDDPDG = "ePAQSelectDDPDG",
  AsendiaDomesticParcelMaxUnder70lb = "AsendiaDomesticParcelMaxUnder70lb",
}

export enum PassportGlobalServices {
  PriorityDdpDelcon = "PriorityDdpDelcon",
  PriorityDdp = "PriorityDdp",
  epacketDdp = "epacketDdp",
  ExpressDdp = "ExpressDdp",
  PriorityDduDelcon = "PriorityDduDelcon",
  PriorityDdu = "PriorityDdu",
  StandardDduDelcon = "StandardDduDelcon",
  ExpeditedDdpDelcon = "ExpeditedDdpDelcon",
  ExpeditedDduDelcon = "ExpeditedDduDelcon",
  ExpressDdpDelcon = "ExpressDdpDelcon",
  GlobalPlus = "GlobalPlus",
}

export enum UniUniServices {
  Standard = "standard",
}

export enum StallionExpressServices {
  StallionEconomyUsa = "stallion_economy_usa",
  StallionEconomyCa = "stallion_economy_ca",
  UsEconomy = "us_economy",
  UsRegional = "us_regional",
  PrecisionGround = "precision_ground",
}

export enum EcomExpressServices {
  SameDay = "SD",
  NextDay = "ND",
  Standard = "SL0026",
}

export enum ChitChatsServices {
  ChitChatsUsEdge = "chit_chats_us_edge",
  ChitChatsUsConnect = "chit_chats_us_connect",
  ChitChatsUsSelect = "chit_chats_us_select",
  ChitChatsSelect = "chit_chats_select",
  ChitChatsSlim = "chit_chats_slim",
  ChitChatsCanadaTracked = "chit_chats_canada_tracked",
  ChitChatsInternationalTracked = "chit_chats_international_tracked",
}

export enum DhlPaketServices {
  Kleinpaket = "DhlKleinpaket",
  Paket = "DhlPaket",
}

export enum DhlParcelServices {
  ForYou = "DhlParcelForYou",
}

export enum DpdServices {
  Home = "DpdHome",
}

// EasyPost DpdNlAccount service strings.
export enum DpdNlServices {
  Classic = "DPDNL_CLASSIC",
  Express10 = "EXPRESS_10",
  Express12 = "EXPRESS_12",
  Express18 = "EXPRESS_18",
  InternationalExpress = "INTERNATIONAL_EXPRESS",
  Home = "DPDNL_HOME",
  ToShop = "DPDNL_TO_SHOP",
}

export enum ColisPriveServices {
  Home = "ColisPriveHome",
}

export enum BpostServices {
  BpackBussable = "BpostBpackBussable",
  Bpack24hPro = "BpostBpack24hPro",
}

export enum CorreosServices {
  Home = "CorreosHome",
}

export enum PosteItalianeServices {
  Home = "PosteItalianeHome",
}

export enum AustriaPostServices {
  Home = "AustriaPostHome",
  SmallParcel = "AustriaPostSmallParcel",
}

export enum EvriServices {
  Ireland = "EvriIreland",
  Uk48 = "EvriUk48",
}

export enum PostNLServices {
  Letterbox = "PostNLLetterbox",
  BelgiumParcels = "PostNLBelgiumParcels",
  InternationalPacket = "PostNLInternationalPacket",
  InternationalPacketTracked = "PostNLInternationalPacketTracked",
}

export enum IntelcomServices {
  Standard = "IntelcomStandard",
}

export enum FleetOpticsServices {
  Express = "FleetOpticsExpress",
  Ground = "FleetOpticsGround",
}

export enum IcsCourierServices {
  Express = "IcsExpress",
  Ground = "IcsGround",
}

export enum WhizDeliveryServices {
  Standard = "WhizDeliveryStandard",
  NextDay = "WhizDeliveryNextDay",
}

export enum TiktokShopServices {
  CollectionByTiktok = "TiktokShopCollectionByTiktok",
}

export enum ETAExpressServices {
  ETAExpress = "ETAExpress",
}

export enum LandmarkGlobalServices {
  Delivery = "Delivery", // Generic service for Landmark Global
}

export enum OnTracServices {
  Ground = "GRND",
}

export enum GlobalEServices {
  Delivery = "Delivery", // Generic service for Global-E
}

export enum UpsMailInnovationsServices {
  StandardFlats = "StandardFlats",
  ParcelSelectLightweightIrreg = "ParcelSelectLightweight-Irreg",
  ParcelSelect = "ParcelSelect",
  BPMParcels = "BPMParcels",
  BPMFlats = "BPMFlats",
  MediaMail = "MediaMail",
  MarketingParcels = "MarketingParcels",
  EconomyMailInnovations = "EconomyMailinnovations",
  ExpeditedMailInnovations = "ExpeditedMailinnovations",
  First = "First",
  Priority = "Priority",
  PriorityMailInnovations = "PriorityMailinnovations",
  SingleReturns = "SingleReturns",
}

export enum GofoServices {
  ParcelPickup = "GOFO_PARCEL_PICKUP",
  CanadaDomesticEconomyEast = "CADOECOEA",
  CanadaDomesticEconomyWest = "CADOECOWE",
  UsaToCanadaEconomyNortheast = "CAECONE",
  UsaToCanadaEconomyWest = "CAECOWE",
  EconomyCentral = "ECOCE",
  EconomyEast = "ECOEA",
  EconomyNortheast = "ECONE",
  EconomySouth = "ECOSO",
  EconomyWest = "ECOWE",
  UsaToEuEconomyNortheast = "EUECONE",
  ExpeditedNortheast = "EXPNE",
  ExpeditedWest = "EXPWE",
  RegionalCentral = "REGCE",
  RegionalCharlotte = "REGCLT",
  RegionalCincinnati = "REGCVG",
  RegionalDenver = "REGDE",
  RegionalEast = "REGEA",
  RegionalNortheast = "REGNE",
  RegionalNewJersey = "REGNJ",
  RegionalOrlando = "REGORL",
  RegionalSoutheast = "REGSE",
  RegionalSanFrancisco = "REGSF",
  RegionalSouth = "REGSO",
  RegionalWest = "REGWE",
  StandardWest = "STDWE",
  StandardSaltLakeCity = "STDSL",
  StandardCentral = "STDCE",
  StandardNewJersey = "STDNJ",
  StandardNortheast = "STDNE",
  StandardEast = "STDEA",
  StandardSoutheast = "STDSE",
  StandardSouth = "STDSO",
  CanadaToUsaEconomyEast = "USECOCA",
  UsaToUkEconomyNortheast = "GBECONE",
  ExpeditedNewJersey = "EXPNJ",
}

export enum CanparServices {
  Ground = "CanparGround",
  SelectLetter = "CanparSelectLetter",
  SelectPak = "CanparSelectPak",
  Select = "CanparSelect",
  OvernightLetter = "CanparOvernightLetter",
  OvernightPak = "CanparOvernightPak",
  Overnight = "CanparOvernight",
  USALetter = "CanparUSALetter",
  USAPak = "CanparUSAPak",
  SelectUSA = "CanparSelectUSA",
}

export enum P2PServices {
  ParcelFlexStd = "PARCEL_FLEX_STD",
  ParcelFlexAdv = "PARCEL_FLEX_ADV",
  ParcelFlexAdvPl = "PARCEL_FLEX_ADV_PL",
  ExpressGnd = "EXPRESS_GND",
  Express1D = "EXPRESS_1D",
  Express2D = "EXPRESS_2D",
  Express3D = "EXPRESS_3D",
  IntlCa = "INTL_CA",
  IntlExpress = "INTL_EXPRESS",
  IntlStd = "INTL_STD",
  FdxGroundHome = "FDX_GROUND_HOME",
  UsGndAdvantage = "US_GND_ADVANTAGE",
}

const ServiceSchema_ = z
  .union([
    createEnum(FedexServices, { enumName: "FedexServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(UpsServices, { enumName: "UpsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(UspsServices, { enumName: "UspsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AmazonServices, { enumName: "AmazonServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(VehoServices, { enumName: "VehoServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(ApcServices, { enumName: "ApcServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(FirstMileServices, { enumName: "FirstMileServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AustraliaPostServices, {
      enumName: "AustraliaPostServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(PurolatorServices, { enumName: "PurolatorServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(GlsServices, { enumName: "GlsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AsendiaServices, { enumName: "AsendiaServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(RoyalMailV3Services, { enumName: "RoyalMailV3Services" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(PassportGlobalServices, {
      enumName: "PassportGlobalServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(EPostGlobalServices, { enumName: "EPostGlobalServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(LandmarkGlobalServices, {
      enumName: "LandmarkGlobalServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(ETAExpressServices, { enumName: "ETAExpressServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(OnTracServices, { enumName: "OnTracServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(GlobalEServices, { enumName: "GlobalEServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(UpsMailInnovationsServices, {
      enumName: "UpsMailInnovationsServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(UniUniServices, { enumName: "UniUniServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(StallionExpressServices, {
      enumName: "StallionExpressServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(EcomExpressServices, { enumName: "EcomExpressServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(ChitChatsServices, { enumName: "ChitChatsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlPaketServices, { enumName: "DhlPaketServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlParcelServices, { enumName: "DhlParcelServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DpdServices, { enumName: "DpdServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DpdNlServices, { enumName: "DpdNlServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(ColisPriveServices, { enumName: "ColisPriveServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(BpostServices, { enumName: "BpostServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(CorreosServices, { enumName: "CorreosServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(PosteItalianeServices, {
      enumName: "PosteItalianeServices",
    }).meta({ privacy: privacy.nonPii }),
    createEnum(AustriaPostServices, { enumName: "AustriaPostServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(EvriServices, { enumName: "EvriServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(PostNLServices, { enumName: "PostNLServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(IntelcomServices, { enumName: "IntelcomServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(FleetOpticsServices, { enumName: "FleetOpticsServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(IcsCourierServices, { enumName: "IcsCourierServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(WhizDeliveryServices, { enumName: "WhizDeliveryServices" }).meta(
      { privacy: privacy.nonPii },
    ),
    createEnum(TiktokShopServices, { enumName: "TiktokShopServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(CanparServices, { enumName: "CanparServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(GofoServices, { enumName: "GofoServices" }).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(P2PServices, { enumName: "P2PServices" }).meta({
      privacy: privacy.nonPii,
    }),
  ])
  .meta({ privacy: privacy.structured });
export type Service = z.infer<typeof ServiceSchema_>;

const CorrelatedCarrierServiceSchema_ = z
  .union([
    z
      .object({
        carrier: createEnum([Carriers.FEDEX, Carriers.FEDEX_SMART_POST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(FedexServices, { enumName: "FedexServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.UPS, Carriers.UPS_SUREPOST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(UpsServices, { enumName: "UpsServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum(
          [Carriers.USPS, Carriers.USPS_RETURNS, Carriers.USPS_SHIP],
          { enumName: "Carriers", isPartial: true, originalEnum: Carriers },
        ).meta({ privacy: privacy.nonPii }),
        service: createEnum(UspsServices, { enumName: "UspsServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.AMAZON], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(AmazonServices, {
          enumName: "AmazonServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DHL_EXPRESS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DhlExpressServices, {
          enumName: "DhlExpressServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.VEHO], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(VehoServices, { enumName: "VehoServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.APC], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(ApcServices, { enumName: "ApcServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.FIRST_MILE], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(FirstMileServices, {
          enumName: "FirstMileServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DHL_ECS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DhlEcsServices, {
          enumName: "DhlEcsServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.AUSTRALIA_POST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(AustraliaPostServices, {
          enumName: "AustraliaPostServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.CANADA_POST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(CanadaPostServices, {
          enumName: "CanadaPostServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.PUROLATOR], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(PurolatorServices, {
          enumName: "PurolatorServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.GLS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(GlsServices, { enumName: "GlsServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ASENDIA], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(AsendiaServices, {
          enumName: "AsendiaServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ROYAL_MAIL_V3], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(RoyalMailV3Services, {
          enumName: "RoyalMailV3Services",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.SPEE_DEE], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(SpeeDeeServices, {
          enumName: "SpeeDeeServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.PASSPORT_GLOBAL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(PassportGlobalServices, {
          enumName: "PassportGlobalServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.EPOST_GLOBAL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(EPostGlobalServices, {
          enumName: "EPostGlobalServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.LANDMARK_GLOBAL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(LandmarkGlobalServices, {
          enumName: "LandmarkGlobalServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ETA_EXPRESS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(ETAExpressServices, {
          enumName: "ETAExpressServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ONTRAC], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(OnTracServices, {
          enumName: "OnTracServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.GLOBALE], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(GlobalEServices, {
          enumName: "GlobalEServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.UPS_MAIL_INNOVATIONS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(UpsMailInnovationsServices, {
          enumName: "UpsMailInnovationsServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.PUROLATOR], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(PurolatorServices, {
          enumName: "PurolatorServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.UNIUNI], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(UniUniServices, {
          enumName: "UniUniServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.STALLION_EXPRESS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(StallionExpressServices, {
          enumName: "StallionExpressServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ECOM_EXPRESS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(EcomExpressServices, {
          enumName: "EcomExpressServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.CHITCHATS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(ChitChatsServices, {
          enumName: "ChitChatsServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DHL_PAKET], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DhlPaketServices, {
          enumName: "DhlPaketServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DHL_PARCEL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DhlParcelServices, {
          enumName: "DhlParcelServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DPD], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DpdServices, { enumName: "DpdServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.DPD_NL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(DpdNlServices, { enumName: "DpdNlServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.COLIS_PRIVE], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(ColisPriveServices, {
          enumName: "ColisPriveServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.BPOST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(BpostServices, { enumName: "BpostServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.CORREOS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(CorreosServices, {
          enumName: "CorreosServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.POSTE_ITALIANE], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(PosteItalianeServices, {
          enumName: "PosteItalianeServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.AUSTRIA_POST], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(AustriaPostServices, {
          enumName: "AustriaPostServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.EVRI], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(EvriServices, { enumName: "EvriServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.POST_NL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(PostNLServices, {
          enumName: "PostNLServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.INTELCOM], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(IntelcomServices, {
          enumName: "IntelcomServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.FLEET_OPTICS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(FleetOpticsServices, {
          enumName: "FleetOpticsServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.ICS_COURIER], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(IcsCourierServices, {
          enumName: "IcsCourierServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.WHIZ_DELIVERY], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(WhizDeliveryServices, {
          enumName: "WhizDeliveryServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.TIKTOK_SHOP], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(TiktokShopServices, {
          enumName: "TiktokShopServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.CANPAR_EXPRESS], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(CanparServices, {
          enumName: "CanparServices",
        }).meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.GOFO], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(GofoServices, { enumName: "GofoServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        carrier: createEnum([Carriers.P2P_GLOBAL], {
          enumName: "Carriers",
          isPartial: true,
          originalEnum: Carriers,
        }).meta({ privacy: privacy.nonPii }),
        service: createEnum(P2PServices, { enumName: "P2PServices" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });
export const CorrelatedCarrierServiceSchema: z.ZodType<CorrelatedCarrierService> =
  CorrelatedCarrierServiceSchema_;
export type CorrelatedCarrierService = {} & z.infer<
  typeof CorrelatedCarrierServiceSchema_
>;

export function getAllServicesArray() {
  return [
    ...Object.values(FedexServices),
    ...Object.values(UpsServices),
    ...Object.values(UspsServices),
    ...Object.values(AmazonServices),
    ...Object.values(DhlExpressServices),
    ...Object.values(VehoServices),
    ...Object.values(ApcServices),
    ...Object.values(FirstMileServices),
    ...Object.values(AustraliaPostServices),
    ...Object.values(CanadaPostServices),
    ...Object.values(PurolatorServices),
    ...Object.values(DhlEcsServices),
    ...Object.values(GlsServices),
    ...Object.values(AsendiaServices),
    ...Object.values(RoyalMailV3Services),
    ...Object.values(SpeeDeeServices),
    ...Object.values(PassportGlobalServices),
    ...Object.values(EPostGlobalServices),
    ...Object.values(LandmarkGlobalServices),
    ...Object.values(ETAExpressServices),
    ...Object.values(OnTracServices),
    ...Object.values(GlobalEServices),
    ...Object.values(UpsMailInnovationsServices),
    ...Object.values(UniUniServices),
    ...Object.values(StallionExpressServices),
    ...Object.values(EcomExpressServices),
    ...Object.values(ChitChatsServices),
    ...Object.values(DhlPaketServices),
    ...Object.values(DhlParcelServices),
    ...Object.values(DpdServices),
    ...Object.values(DpdNlServices),
    ...Object.values(ColisPriveServices),
    ...Object.values(BpostServices),
    ...Object.values(CorreosServices),
    ...Object.values(PosteItalianeServices),
    ...Object.values(AustriaPostServices),
    ...Object.values(EvriServices),
    ...Object.values(PostNLServices),
    ...Object.values(IntelcomServices),
    ...Object.values(FleetOpticsServices),
    ...Object.values(IcsCourierServices),
    ...Object.values(WhizDeliveryServices),
    ...Object.values(TiktokShopServices),
    ...Object.values(CanparServices),
    ...Object.values(GofoServices),
    ...Object.values(P2PServices),
  ];
}

export const Service = getAllServicesArray();

export const ServiceSchema: z.ZodType<Service> = ServiceSchema_;
